
import { useState, useEffect } from "react";
import { supabase } from "@/lib/supabase";
import { safeGetItem, safeRemoveItem } from "@/utils/storage";
import { useToast } from "@/hooks/use-toast";

export const useAuthState = (onSignedIn: (savedPrompt: string | null) => void) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const newAuthState = !!session;
        console.log('Initial auth state:', { newAuthState, email: session?.user?.email });
        
        setIsAuthenticated(newAuthState);
        setUserEmail(session?.user?.email || null);
        setIsInitialized(true);
        
        if (newAuthState) {
          const savedPrompt = safeGetItem('pendingMeetingPrompt');
          console.log('Checking for saved prompt in initializeAuth:', savedPrompt);
          if (savedPrompt) {
            await onSignedIn(savedPrompt);
          }
        }
      } catch (error) {
        console.error('Error initializing auth:', error);
        toast({
          title: "Authentication Error",
          description: "Failed to initialize authentication",
          variant: "destructive"
        });
        setIsInitialized(true);
      }
    };

    initializeAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('Auth state changed:', { event, email: session?.user?.email });
      const newAuthState = !!session;
      setIsAuthenticated(newAuthState);
      setUserEmail(session?.user?.email || null);

      if (event === 'SIGNED_IN') {
        const savedPrompt = safeGetItem('pendingMeetingPrompt');
        console.log('Checking for saved prompt after sign in:', savedPrompt);
        if (savedPrompt) {
          await onSignedIn(savedPrompt);
        }
      }
    });

    return () => subscription.unsubscribe();
  }, [onSignedIn, toast]);

  return { isAuthenticated, userEmail, isInitialized };
};
